// Shared CSV primitives plus the "Applied Filters" provenance block that every
// download gets prepended. A downloaded file outlives the screen it came from,
// so without this there is no way to tell which date range / filter selection
// produced the numbers in it.
//
// Lives in its own module rather than in widgetExport.ts because Report.ts
// needs it too, and widgetExport.ts pulls in html2canvas/jsPDF/React for its
// image exports — importing that into the report download path would drag all
// of it along for a few lines of string building.

export const csvCell = (val: unknown): string => {
  const s =
    val === null || val === undefined
      ? ""
      : // A nested object/array would otherwise stringify to "[object Object]"
        typeof val === "object"
        ? JSON.stringify(val)
        : String(val);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const titleCase = (key: string): string =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bPa\b/g, "PA");

// Request mechanics the download code injects into `filters` before calling the
// export endpoint — not anything the user picked, so they'd be noise in the
// header (and `top_n: "all"` actively misleads, since it's the opposite of the
// Top-N the user has on screen).
const NON_USER_FILTER_KEYS = new Set(["download_level", "top_n"]);

// The filter bar labels two of these keys differently from the key itself
// (src/components/filters/index.tsx: `period` renders as "Group By",
// `drug_name` as "Products"), so title-casing the key would print "Period" and
// "Drug Name" — names the user never sees on screen. Keep this in step with
// that component.
const FILTER_LABELS: Record<string, string> = {
  start_date: "Start Date",
  end_date: "End Date",
  period: "Group By",
  drug_name: "Products",
};

// Filter-bar order, so the block reads the way the panel does.
const PREFERRED_ORDER = ["start_date", "end_date", "period", "drug_name"];

export const filterLabel = (key: string): string =>
  FILTER_LABELS[key] ?? titleCase(key);

// The filter bar stores dates as yyyy-MM-dd (src/helpers/Filter.ts DATE_FORMAT)
// — reformatted to MM/DD/YYYY here to match how dates are shown everywhere
// else in the app (DataContextBar, the date pickers).
const DATE_FILTER_KEYS = new Set(["start_date", "end_date"]);
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatFilterDate = (val: string): string => {
  const m = ISO_DATE.exec(val);
  // Only reformat what's actually recognized as yyyy-MM-dd — anything else
  // (a differently-shaped value the backend might send) is passed through
  // unchanged rather than guessed at.
  return m ? `${m[2]}/${m[3]}/${m[1]}` : val;
};

// An unset filter isn't "no filter" to a reader — it means the export covers
// everything, which is what the filter bar's empty Products/Group By selects
// mean. Rendering it blank would read as missing data instead.
export const filterValue = (key: string, val: unknown): string => {
  if (val === null || val === undefined || val === "") return "All";
  if (Array.isArray(val)) return val.length === 0 ? "All" : val.join("; ");
  const s = String(val);
  return DATE_FILTER_KEYS.has(key) ? formatFilterDate(s) : s;
};

export const getAppliedFilterEntries = (
  filters: Record<string, unknown> | undefined,
): Array<[string, unknown]> => {
  if (!filters) return [];
  // Empty values are kept deliberately: an unfiltered Products/Group By still
  // has to appear (as "All"), or the block silently under-reports what the
  // export covers.
  const keys = Object.keys(filters).filter(
    (key) => !NON_USER_FILTER_KEYS.has(key),
  );
  const ordered = [
    ...PREFERRED_ORDER.filter((key) => keys.includes(key)),
    ...keys.filter((key) => !PREFERRED_ORDER.includes(key)),
  ];
  return ordered.map((key) => [key, filters[key]]);
};

// The block as [label, value] pairs — what the xlsx writer needs, since it
// works in cells rather than pre-joined text.
export const buildAppliedFiltersRows = (
  filters: Record<string, unknown> | undefined,
): Array<[string, string]> =>
  getAppliedFilterEntries(filters).map(([key, val]) => [
    filterLabel(key),
    filterValue(key, val),
  ]);

// The block itself, as CSV lines — caller decides where it goes. Returns []
// when nothing is applied, so no empty header is emitted.
export const buildAppliedFiltersLines = (
  filters: Record<string, unknown> | undefined,
): string[] => {
  const entries = getAppliedFilterEntries(filters);
  if (entries.length === 0) return [];
  return [
    "Applied Filters",
    "Filter,Value",
    ...entries.map(
      ([key, val]) =>
        `${csvCell(filterLabel(key))},${csvCell(filterValue(key, val))}`,
    ),
    "",
  ];
};

// Prepends the block to an already-built CSV string. The BOM has to stay the
// very first bytes of the file or Excel shows it as text in cell A1 instead of
// reading the file as UTF-8, so it is lifted off and re-applied.
export const prependAppliedFilters = (
  csv: string,
  filters: Record<string, unknown> | undefined,
): string => {
  const lines = buildAppliedFiltersLines(filters);
  if (lines.length === 0) return csv;
  const hasBom = csv.startsWith("﻿");
  const body = hasBom ? csv.slice(1) : csv;
  return `${hasBom ? "﻿" : ""}${lines.join("\r\n")}\r\n${body}`;
};
